/** Coordinates one exact capture request without animation-frame polling. */
export class FrameReadiness {
  ready = false;
  progress = -1;
  private requested = 0;
  private pending: {
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | undefined;

  request(progress: number): boolean {
    if (this.ready && this.progress === progress) return false;
    if (progress !== this.requested) this.cancel("Render frame request was superseded.");
    this.requested = progress;
    this.ready = false;
    return true;
  }

  complete(progress: number): void {
    if (progress !== this.requested) return;
    this.progress = progress;
    this.ready = true;
    if (this.pending) {
      clearTimeout(this.pending.timer);
      this.pending.resolve();
      this.pending = undefined;
    }
  }

  wait(progress: number, timeoutMs: number): Promise<void> {
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return Promise.reject(new RangeError("Invalid frame timeout."));
    if (progress !== this.requested) return Promise.reject(new Error("Render frame request was superseded."));
    if (this.ready && this.progress === progress) return Promise.resolve();
    if (this.pending) return this.pending.promise;
    let resolve!: () => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<void>((done, failed) => { resolve = done; reject = failed; });
    const timer = setTimeout(() => this.cancel(`Timed out waiting for render frame ${progress}.`), timeoutMs);
    this.pending = { promise, resolve, reject, timer };
    return promise;
  }

  reset(): void {
    this.cancel("Render trace was replaced.");
    this.requested = 0;
    this.progress = -1;
    this.ready = false;
  }

  cancel(message: string): void {
    if (this.pending) {
      clearTimeout(this.pending.timer);
      this.pending.reject(new Error(message));
      this.pending = undefined;
    }
  }
}
